import ConversationItemType from 'models/conversation_item_type';
import CreateChatComposition from 'scripts/application/actions/composition/create_chat_composition';
import CreateComposition from 'scripts/application/actions/composition/lib/create_composition';
import CreateEmailComposition from 'scripts/application/actions/composition/create_email_composition';
import CreateFbMessageComposition from 'scripts/application/actions/composition/create_fb_message_composition';
import CreateFacebookCommentReplyComposition from 'actions/composition/create_facebook_comment_reply_composition';
import CreateInstagramPublicReplyComposition from 'actions/composition/create_instagram_public_reply_composition';
import CreateInstagramComposition from 'scripts/application/actions/composition/create_instagram_composition';
import CreateNoteComposition from 'scripts/application/actions/composition/create_note_composition';
import CreatePhoneCallComposition from 'scripts/application/actions/composition/create_phone_call_composition';
import CreateSmsComposition from 'scripts/application/actions/composition/create_sms_composition';
import CreateTweetComposition from 'scripts/application/actions/composition/create_tweet_composition';
import CreateWhatsAppComposition from 'scripts/application/actions/composition/create_whatsapp_composition';
import { getActiveChatSessionIdFromHistory } from 'actions/communication_session/session_finder';
import { getLatestManualItem, isCustomerItem } from 'scripts/application/lib/conversation_history_helpers';
import InteractionType from 'models/interaction_type';
import { MessageChannelType, ModerationStatus } from 'models/conversation_message';

export default class ActivateDefaultResponse extends CreateComposition {
  _createContent() {
    return this.content.createResponse();
  }

  run({ conversationId }) {
    if (!this.context.stores.appFeatures.get().isEnabled('conversation:outbound')) {
      this.context.executeAction(CreateNoteComposition, { conversationId });
      return;
    }

    const customerId = this.context.stores.currentLocation.get().customerId;

    const sessionId = getSessionId(this.customerId, this.context, this.customerStores);
    if (sessionId) {
      this.context.executeAction(CreateChatComposition, {
        conversationId,
        sessionId,
      });
      return;
    }

    const { conversationHistory, conversations } = this.context.stores.customers.storesFor(customerId);

    const conversation = conversations.findBy({ id: conversationId });
    if (!conversation) {
      return;
    }

    const latestCustomerItem = getLatestManualItem({
      conversationHistory,
      conversationId: conversation.id,
      filter: isCustomerItem,
    });

    if (!latestCustomerItem) {
      this.context.executeAction(CreateNoteComposition, { conversationId });
      return;
    }

    let contentType = latestCustomerItem.contentType();
    if (contentType === ConversationItemType.CONVERSATION_MESSAGE) {
      contentType = latestCustomerItem.content.channel;
    }

    // WebForms default to emails
    if (contentType === ConversationItemType.WEB_FORM || contentType === ConversationItemType.EMAIL) {
      this.context.executeAction(CreateEmailComposition, { conversationId });
      return;
    }

    if (contentType === ConversationItemType.SMS) {
      this.context.executeAction(CreateSmsComposition, { conversationId });
      return;
    }

    if (contentType === ConversationItemType.TWITTER) {
      const customerTwitterHandle = latestCustomerItem.content.meta?.handle;
      const customerTwitterAddress = latestCustomerItem.content.customerAddress;
      this.context.executeAction(CreateTweetComposition, {
        conversationId,
        customerTwitterHandle,
        customerTwitterAddress,
      });
      return;
    }

    if (contentType === ConversationItemType.VOICE_AI_RECORDING) {
      this.context.executeAction(CreatePhoneCallComposition, {
        conversationId,
        phoneNumber: latestCustomerItem.content.customerNumber,
      });
      return;
    }

    if (contentType === ConversationItemType.VOICEMAIL) {
      this.context.executeAction(CreatePhoneCallComposition, {
        conversationId,
        phoneNumber: latestCustomerItem.content.customerNumber,
      });
      return;
    }

    if (contentType === MessageChannelType.INSTAGRAM_DIRECT) {
      this.context.executeAction(CreateInstagramComposition, { conversationId });
      return;
    }

    if (contentType === MessageChannelType.INSTAGRAM_COMMENT) {
      const moderationStatus = latestCustomerItem.content.getModerationStatus();
      if (moderationStatus === ModerationStatus.DELETED || moderationStatus === ModerationStatus.HIDDEN) {
        this.context.executeAction(CreateNoteComposition, { conversationId });
        return;
      }

      this.context.executeAction(CreateInstagramPublicReplyComposition, {
        conversationId,
        conversationItem: latestCustomerItem,
      });
      return;
    }

    if (contentType === MessageChannelType.FB_COMMENT) {
      const moderationStatus = latestCustomerItem.content.getModerationStatus();
      if (
        !this.context.stores.appFeatures.get().isEnabled('facebookCommentReplies') ||
        moderationStatus === ModerationStatus.DELETED ||
        moderationStatus === ModerationStatus.HIDDEN
      ) {
        this.context.executeAction(CreateNoteComposition, { conversationId });
        return;
      }

      this.context.executeAction(CreateFacebookCommentReplyComposition, {
        conversationId,
        conversationItem: latestCustomerItem,
      });
      return;
    }

    if (
      contentType === ConversationItemType.FB_MESSAGE_INCOMING ||
      contentType === ConversationItemType.FB_MESSAGE_OUTGOING
    ) {
      this.context.executeAction(CreateFbMessageComposition, { conversationId });
      return;
    }

    // Generic message items will rely on the channel type
    if (contentType === MessageChannelType.WHATSAPP) {
      this.context.executeAction(CreateWhatsAppComposition, { conversationId });
      return;
    }

    // default is to create a note
    this.context.executeAction(CreateNoteComposition, { conversationId });
  }

  get currentAgentId() {
    return this.context.stores.currentAgent.get().id;
  }
}

export function getSessionId(customerId, context, customerStores) {
  const activeSession = context.stores.activeSessions.findBy({ customer: { id: customerId } });
  const chatSession = activeSession?.queueItem?.getSessionByType(InteractionType.CHAT);
  return chatSession?.id ?? getActiveChatSessionIdFromHistory(customerStores);
}
